import { Controller } from "react-hook-form";
import type { Control, FieldErrors } from "react-hook-form";
import type { ChangeEvent } from "react";
import type { FormFieldConfig } from "../../../types/uiTypes";
import styles from "./RadioField.module.css";

type RadioFieldProps = {
  field: FormFieldConfig & {
    options: Array<{ value: string; label: string }>;
    onChange?: (value: string | number) => void;
  };
  control: Control<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
};

export function RadioField({ field, control, errors }: RadioFieldProps) {
  const error = errors[field.name]?.message;
  const errorMessage = typeof error === "string" ? error : undefined;
  const errorId = `${field.name}-error`;
  const helperId = `${field.name}-helper`;
  const describedBy = errorMessage ? errorId : field.helperText ? helperId : undefined;

  return (
    <div className={styles.formField}>
      <fieldset
        className={styles.radioFieldset}
        aria-invalid={!!errorMessage}
        aria-describedby={describedBy}
      >
        <legend className={styles.label}>
          {field.label}{" "}
          {field.required && (
            <span className={styles.required} aria-label="required">
              *
            </span>
          )}
        </legend>
        <Controller
          name={field.name}
          control={control}
          render={({ field: controllerField }) => (
            <div className={styles.radioGroup}>
              {field.options.map(option => (
                <label key={option.value} className={styles.radioLabel}>
                  <input
                    type="radio"
                    {...controllerField}
                    value={option.value}
                    checked={controllerField.value === option.value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      controllerField.onChange(value);
                      if (field.onChange) {
                        field.onChange(value);
                      }
                    }}
                    disabled={field.disabled}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>{option.label}</span>
                </label>
              ))}
            </div>
          )}
        />
      </fieldset>
      {errorMessage && (
        <span id={errorId} className={styles.errorText} role="alert" aria-live="polite">
          {errorMessage}
        </span>
      )}
      {field.helperText && !errorMessage && (
        <span id={helperId} className={styles.helperText}>
          {field.helperText}
        </span>
      )}
    </div>
  );
}
